// Track.jsx - The customer's page for one order. The single most important screen
// on the customer site, because it is the one they check at 11pm wondering where
// their grandmother's ring is.
//
// The rules it is built to: accurate over pretty, always say what happens next,
// and always link to the carrier so nobody has to take our word for anything.
//
// LEGEND
//   1. StageRail     where the order is, in plain language
//   2. OfferCard     the offer, itemised, with accept and decline
//   3. MediaWall     the unboxing video and item photos
//   4. Shipments     carrier status with a link to the carrier
//   5. Timeline      the customer-visible event log
//   6. Track         the page

import { useEffect, useState } from "react";
import { useParams, Link } from "react-router";
import clsx from "clsx";

import { Seo } from "../lib/seo.jsx";
import { useAsync } from "../lib/hooks.js";
import { publicApi } from "../lib/api.js";
import * as analytics from "../lib/analytics.js";
import { remember, remembered, forget } from "../lib/session.js";
import { money, grams, day, dayTime, ago, METAL_LABELS, TEST_LABELS, PAYOUT_LABELS } from "../lib/format.js";
import { Button, Panel, Alert, Loading, Modal, Tag, Empty, Textarea, useToast } from "../ui/kit.jsx";
import { Check, ArrowUpRight, Truck, Video, Clock, Shield, Phone, AlertIcon, CheckCircle } from "../ui/icons.jsx";
import { Reveal, StaggerBoxed } from "../ui/motion.jsx";
import { TruckRoute } from "../ui/illustrations.jsx";
import PayoutForm from "./PayoutForm.jsx";

////////// 1. STAGE RAIL //////////
// Five stages, not fourteen statuses. A customer does not care about the
// difference between "evaluating" and "offer sent" until the offer arrives.

const RAIL = [
  { key: "intake", label: "Request", detail: "We read it and approve the kit" },
  { key: "outbound", label: "Kit sent", detail: "On its way to you" },
  { key: "inbound", label: "Your items", detail: "Travelling back to us, insured" },
  { key: "evaluation", label: "Evaluated", detail: "Opened on camera, weighed and tested" },
  { key: "offer", label: "Your offer", detail: "Yours to accept or decline" },
  { key: "closed", label: "Done", detail: "Paid or returned" },
];

function StageRail({ stage }) {
  const currentIndex = Math.max(0, RAIL.findIndex((entry) => entry.key === stage));

  return (
    <ol className="grid grid-cols-2 gap-px bg-rule border border-rule sm:grid-cols-3 lg:grid-cols-6">
      {RAIL.map((entry, index) => {
        const state = index < currentIndex ? "done" : index === currentIndex ? "current" : "todo";
        return (
          <li key={entry.key} className={clsx("bg-paper p-3.5 sm:p-4 transition-colors duration-500", state === "current" && "bg-bronze-wash")}>
            <div className="flex items-center gap-2 mb-2">
              <span className={clsx(
                "flex items-center justify-center w-[18px] h-[18px] rounded-full text-[10px] font-bold border shrink-0",
                state === "done" && "bg-bronze border-bronze text-white",
                state === "current" && "border-bronze text-bronze-dark",
                state === "todo" && "border-rule text-faint"
              )}>
                {state === "done" ? <Check size={11} /> : index + 1}
              </span>
              <p className={clsx("text-[13.5px] font-semibold", state === "todo" ? "text-faint" : "text-ink")}>
                {entry.label}
              </p>
            </div>
            <p className={clsx("text-[12.5px] leading-snug", state === "todo" ? "text-faint" : "text-muted")}>
              {entry.detail}
            </p>
          </li>
        );
      })}
    </ol>
  );
}

////////// 2. OFFER //////////

function OfferCard({ order, token, onChanged }) {
  const toast = useToast();
  const [confirming, setConfirming] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const respond = async (response) => {
    setBusy(true);
    try {
      await publicApi.respondToOffer(token, { response, reason: reason || undefined });
      // The acceptance only. A declined offer is a real outcome and it is not a
      // conversion: counting it would teach an ad platform that a decline was a
      // success.
      if (response === "accepted") analytics.trackOfferAccepted(order.number);
      toast.success(response === "accepted" ? "Offer accepted. We will be in touch straight away." : "Understood. Everything comes back to you, insured, at our cost.");
      setConfirming(null);
      onChanged();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const { offer, appraised } = order;
  const expires = offer.expiresAt ? day(offer.expiresAt) : null;

  return (
    <section id="offer" className="scroll-mt-20">
      <Panel className="overflow-hidden">
        <header className="px-5 sm:px-7 py-5 border-b border-rule flex flex-wrap items-end justify-between gap-3 sm:gap-4">
          <div>
            <p className="eyebrow">Your offer</p>
            <p className="display display-xl text-ink mt-1 num leading-none">{money(offer.amount)}</p>
            {expires && <p className="hint mt-2.5 flex items-center gap-1.5"><Clock size={14} /> Held at this price until {expires}</p>}
          </div>
          {offer.open && <Tag tone="bronze">Waiting on you</Tag>}
          {offer.response === "accepted" && <Tag tone="positive"><Check size={12} /> Accepted {ago(offer.respondedAt)}</Tag>}
          {offer.response === "declined" && <Tag>Declined {ago(offer.respondedAt)}</Tag>}
        </header>

        {appraised?.length > 0 && (
          <div className="border-b border-rule">
            <table className="stack-table w-full text-[14px]">
              <thead>
                <tr className="border-b border-rule-soft">
                  <th className="text-left eyebrow font-semibold px-5 sm:px-7 py-2.5">Piece</th>
                  <th className="text-right eyebrow font-semibold px-3 py-2.5 hidden sm:table-cell">Gross</th>
                  <th className="text-right eyebrow font-semibold px-3 py-2.5 hidden md:table-cell">Stones</th>
                  <th className="text-right eyebrow font-semibold px-3 py-2.5">Metal content</th>
                  <th className="text-right eyebrow font-semibold px-5 sm:px-7 py-2.5">Amount</th>
                </tr>
              </thead>
              <tbody>
                {appraised.map((lot) => (
                  <tr key={lot.id} className="border-b border-rule-soft last:border-0">
                    <td data-label="" className="px-5 sm:px-7 py-3">
                      <span className="block text-ink font-medium">{lot.label || `${METAL_LABELS[lot.metal]} lot`}</span>
                      <span className="block text-[12.5px] text-faint mt-0.5">
                        {METAL_LABELS[lot.metal]} {lot.purity ? `· ${lot.purity.toUpperCase()}` : ""} {lot.testMethod ? `· ${TEST_LABELS[lot.testMethod]}` : ""}
                      </span>
                    </td>
                    <td data-label="Gross" className="px-3 py-3 text-right num text-muted sm:table-cell">{grams(lot.grossWeight)}</td>
                    <td data-label="Stones" className="px-3 py-3 text-right num text-muted md:table-cell">{lot.stoneWeight ? `− ${grams(lot.stoneWeight)}` : "—"}</td>
                    <td data-label="Metal content" className="px-3 py-3 text-right num text-ink font-medium">{grams(lot.fineWeight)}</td>
                    <td data-label="Amount" className="px-5 sm:px-7 py-3 text-right num font-semibold text-ink">{money(lot.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {offer.note && (
          <div className="px-5 sm:px-7 py-4 border-b border-rule bg-surface/60">
            <p className="eyebrow mb-1.5">A note from the appraiser</p>
            <p className="text-[14.5px] text-ink-soft leading-relaxed whitespace-pre-line">{offer.note}</p>
          </div>
        )}

        {offer.open ? (
          <div className="px-5 sm:px-7 py-6">
            <div className="flex flex-col xs:flex-row flex-wrap gap-3">
              <Button size="lg" onClick={() => setConfirming("accepted")}>Accept {money(offer.amount)}</Button>
              <Button size="lg" variant="quiet" onClick={() => setConfirming("declined")}>Decline and send it back</Button>
            </div>
            <p className="hint mt-4 leading-relaxed measure-wide">
              Declining costs you nothing. We pack everything exactly as it arrived and ship it back insured
              at our expense, usually the next business day.
            </p>
          </div>
        ) : (
          <div className="px-5 sm:px-7 py-5 bg-surface/60 text-[14px] text-muted">
            {offer.response === "accepted"
              ? "Accepted. Your payment details are below."
              : "Declined. Your items are on their way back to you, insured, at our cost."}
          </div>
        )}
      </Panel>

      <Modal
        open={Boolean(confirming)}
        onClose={() => !busy && setConfirming(null)}
        title={confirming === "accepted" ? `Accept ${money(offer.amount)}?` : "Send everything back?"}
        description={confirming === "accepted"
          ? "Once you accept, the sale is final and your items become ours."
          : "We will pack everything exactly as it arrived and ship it back insured, at our cost."}
        footer={
          <>
            <Button variant="quiet" onClick={() => setConfirming(null)} disabled={busy}>Not yet</Button>
            <Button
              variant={confirming === "accepted" ? "primary" : "ink"}
              loading={busy}
              onClick={() => respond(confirming)}
            >
              {confirming === "accepted" ? `Yes, accept ${money(offer.amount)}` : "Yes, send it back"}
            </Button>
          </>
        }
      >
        {confirming === "accepted" ? (
          <ul className="space-y-2.5 text-[14.5px] text-ink-soft">
            {["The sale is final once you accept.", "We will ask how you want to be paid on the next screen.", "Accept before 3pm Eastern on a business day and the transfer goes out today."].map((line) => (
              <li key={line} className="flex gap-2.5"><Check size={17} className="text-bronze shrink-0 mt-0.5" />{line}</li>
            ))}
          </ul>
        ) : (
          <Textarea
            label="Anything we should know? Optional."
            placeholder="It was less than I hoped, or I changed my mind, or nothing at all."
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        )}
      </Modal>
    </section>
  );
}

////////// 3. MEDIA //////////

function MediaWall({ media }) {
  const [open, setOpen] = useState(null);
  if (!media?.length) return null;

  const videos = media.filter((item) => item.kind === "video");
  const images = media.filter((item) => item.kind !== "video");

  return (
    <section>
      <div className="flex items-baseline justify-between gap-4 mb-4">
        <h2 className="display display-md text-ink">Us opening your package</h2>
        <p className="hint flex items-center gap-1.5"><Video size={15} /> Recorded from seal to scale</p>
      </div>

      {videos.map((item) => (
        <div key={item.url} className="mb-3">
          <video controls preload="metadata" playsInline className="w-full bg-ink border border-rule" src={item.url}>
            <track kind="captions" />
          </video>
          {item.label && <p className="hint mt-1.5">{item.label}</p>}
        </div>
      ))}

      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {images.map((item) => (
            <button
              key={item.url}
              type="button"
              onClick={() => setOpen(item)}
              className="relative aspect-4/3 overflow-hidden border border-rule bg-surface group"
            >
              <img src={item.url} alt={item.label || "Item photograph"} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
            </button>
          ))}
        </div>
      )}

      <Modal open={Boolean(open)} onClose={() => setOpen(null)} title={open?.label || "Photograph"} size="lg">
        {open && <img src={open.url} alt={open.label || "Item photograph"} className="w-full" />}
      </Modal>
    </section>
  );
}

////////// 4. SHIPMENTS //////////

function Shipments({ shipments }) {
  if (!shipments?.length) return null;

  return (
    <section>
      <h2 className="display display-md text-ink mb-4">Shipping</h2>
      <div className="space-y-3">
        {shipments.map((shipment) => (
          <Panel key={`${shipment.label}-${shipment.tracking}`} className="p-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Truck size={17} className="text-bronze shrink-0" />
                  <p className="font-semibold text-ink">{shipment.label}</p>
                </div>
                <p className="text-[14px] text-muted mt-1.5">{shipment.statusText || "Waiting on the carrier"}</p>
                {shipment.eta && shipment.status !== "delivered" && (
                  <p className="hint mt-1">Expected {day(shipment.eta)}</p>
                )}
                {shipment.deliveredAt && <p className="hint mt-1">Delivered {dayTime(shipment.deliveredAt)}</p>}
              </div>

              {shipment.carrierUrl && (
                <a href={shipment.carrierUrl} target="_blank" rel="noopener noreferrer" className="btn btn-quiet btn-sm shrink-0">
                  {shipment.carrier} tracking <ArrowUpRight size={14} />
                </a>
              )}
            </div>

            {shipment.tracking && (
              <p className="hint mt-3 num border-t border-rule-soft pt-3">
                Tracking number <span className="text-ink-soft">{shipment.tracking}</span>
              </p>
            )}

            {shipment.scans?.length > 0 && (
              <details className="mt-3">
                <summary className="text-[13px] text-muted cursor-pointer hover:text-ink select-none">
                  Carrier scans ({shipment.scans.length})
                </summary>
                <ul className="mt-3 space-y-2 border-l border-rule pl-4">
                  {[...shipment.scans].reverse().map((scan, index) => (
                    <li key={index} className="text-[13px]">
                      <span className="text-ink-soft">{scan.message}</span>
                      <span className="text-faint"> {scan.where ? `· ${scan.where}` : ""} · {dayTime(scan.at)}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </Panel>
        ))}
      </div>
    </section>
  );
}

////////// 5. TIMELINE //////////

function Timeline({ events }) {
  if (!events?.length) return null;

  return (
    <section>
      <h2 className="display display-md text-ink mb-4">Everything that has happened</h2>
      <ol className="border-l border-rule pl-6 space-y-6">
        {[...events].reverse().map((event) => (
          <li key={event.id} className="relative">
            <span className="absolute -left-[27px] top-1.5 w-2 h-2 rounded-full bg-bronze-soft ring-4 ring-paper" />
            <p className="text-[15px] text-ink font-medium">{event.title}</p>
            {event.detail && <p className="hint mt-1 leading-relaxed whitespace-pre-line">{event.detail}</p>}
            <p className="text-[12.5px] text-faint mt-1">{dayTime(event.at)}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

////////// 6. PAGE //////////

export default function Track() {
  const { token } = useParams();
  const { data: order, error, loading, reload } = useAsync(() => publicApi.track(token), [token]);

  // Following the link from the email is the ordinary way in, and it is proof
  // enough: this browser now knows the order so nobody has to go and find that
  // email again. /track redirects straight here next time.
  //
  // An error means the server no longer recognises this token, so a stale entry
  // is dropped rather than left to send somebody back to a page that is gone.
  useEffect(() => {
    if (order?.number) remember({ number: order.number, token });
    else if (error && remembered()?.token === token) forget();
  }, [order, error, token]);

  if (loading) return <Loading label="Finding your order" className="min-h-[60vh]" />;

  if (error) {
    return (
      <>
        <Seo title="Order not found" path={`/track/${token}`} noIndex />
        <div className="shell-narrow py-20">
          <Empty icon={AlertIcon} title="We could not find that order">
            {error.message}
          </Empty>
          <div className="flex justify-center gap-3">
            <Link to="/track" className="btn btn-primary">Find my order</Link>
            <a href="tel:+1-401-315-4653" className="btn btn-quiet"><Phone size={16} /> Call us</a>
          </div>
        </div>
      </>
    );
  }

  const needsPayout = order.offer?.response === "accepted" && !order.payout?.onFile;
  const paid = Boolean(order.payout?.sentAt);

  return (
    <>
      <Seo title={`Order ${order.number}`} path={`/track/${token}`} noIndex />

      {/* Header */}
      <header className="border-b border-rule bg-surface/40 clip-x">
        <div className="shell py-8 md:py-12">
          <div className="flex flex-wrap items-end justify-between gap-5">
            <Reveal>
              <p className="eyebrow">Order {order.number}</p>
              <h1 className="display display-lg text-ink mt-2">{order.statusLabel}</h1>
              <p className="hint mt-2">Last updated {ago(order.updatedAt)}</p>
            </Reveal>
            <div className="text-left sm:text-right">
              <p className="hint">{order.customer.firstName} {order.customer.lastName}</p>
              <a href="tel:+1-401-315-4653" className="btn btn-quiet btn-sm mt-2">
                <Phone size={14} /> Something looks wrong
              </a>
              {/* This browser remembers the order so /track opens it without
                  asking. Somebody sharing a computer has to be able to say
                  that is not me, or the memory is a trap rather than a
                  convenience. */}
              <p className="hint mt-2">
                <Link to="/track?switch=1" onClick={forget} className="link">Not your order?</Link>
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="shell py-8 md:py-14 space-y-10 md:space-y-12">
        <Reveal from="none"><StageRail stage={order.stage} /></Reveal>

        {/* The one thing to do right now, if there is one. */}
        {order.offer?.open && (
          <Alert tone="warning" title="Your offer is waiting">
            Take as long as you need. The price is held until {day(order.offer.expiresAt)}, and declining is free.
          </Alert>
        )}

        {order.offer && <OfferCard order={order} token={token} onChanged={reload} />}

        {needsPayout && (
          <section id="payout" className="scroll-mt-20">
            <Panel className="p-5 sm:p-7">
              <p className="eyebrow mb-1">Last step</p>
              <h2 className="display display-md text-ink mb-5">Tell us how to pay you</h2>
              <PayoutForm token={token} order={order} onDone={reload} />
            </Panel>
          </section>
        )}

        {paid && (
          <Alert tone="success" title={`${money(order.payout.amount)} sent on ${day(order.payout.sentAt)}`}>
            Paid by {PAYOUT_LABELS[order.payout.method] || order.payout.method}
            {order.payout.reference ? `, reference ${order.payout.reference}` : ""}. Thank you for trusting us with your items.
          </Alert>
        )}

        {/* Only for the few people who asked for cover above the standard amount.
            Everybody else never sees a word about it, because the standard cover
            is automatic and needs no decision from them. */}
        {order.coverage && (
          <Alert
            tone={order.coverage.status === "approved" ? "success" : order.coverage.status === "denied" ? "info" : "warning"}
            title={
              order.coverage.status === "approved" ? `This shipment is covered to ${money(order.coverage.approvedValue)}`
                : order.coverage.status === "denied" ? "We could not cover the amount you asked for"
                : `Your request to cover this for ${money(order.coverage.requestedValue)} is with us`
            }
          >
            {order.coverage.status === "approved"
              ? "You are clear to send. Use the prepaid label, and hand the package to a person at a staffed counter so you have a receipt with a scan on it."
              : order.coverage.status === "denied"
              ? "Your shipment still carries our standard cover, the same as every other package that comes to us. Please call us before you post anything worth more than that."
              : "Please do not post your items until we answer. We usually answer the same business day, by email and by text."}
          </Alert>
        )}

        <MediaWall media={order.media} />
        <Shipments shipments={order.shipments} />

        {/* What you sent, as you described it. Kept so nothing gets lost between
            the request and the appraisal. */}
        {order.declared?.length > 0 && !order.appraised?.length && (
          <section>
            <h2 className="display display-md text-ink mb-4">What you told us you were sending</h2>
            <Panel>
              <ul className="divide-y divide-rule-soft">
                {order.declared.map((item, index) => (
                  <li key={index} className="px-5 py-3.5 flex items-baseline justify-between gap-4">
                    <span className="text-[14.5px] text-ink">{item.description || METAL_LABELS[item.metal]}</span>
                    <span className="hint num shrink-0">
                      {METAL_LABELS[item.metal]}{item.karat ? ` ${item.karat.toUpperCase()}` : ""}
                      {item.approxWeight ? ` · about ${item.approxWeight}${item.unit}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </Panel>
          </section>
        )}

        <Timeline events={order.timeline} />

        <section className="border-t border-rule pt-8">
          <StaggerBoxed as="div" step={90} className="grid gap-6 sm:grid-cols-3">
            {[
              { icon: Shield, title: "Insured throughout", body: "Both directions, from the first carrier scan." },
              { icon: CheckCircle, title: "Nothing is final until you say", body: "Declining is free and everything comes back." },
              { icon: Phone, title: "Talk to a person", body: "(401) 315-4653, Monday to Friday, 9am to 6pm Eastern." },
            ].map(({ icon: Glyph, title, body }) => (
              <div key={title} className="flex gap-3">
                <Glyph size={18} className="text-bronze shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-[14.5px] text-ink">{title}</p>
                  <p className="hint mt-1 leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </StaggerBoxed>
        </section>
      </div>
    </>
  );
}
